/**
 * @fileoverview Shorts Modal
 *
 */

import makePager from './pager.js';

/**
 * @module shortsModal
 * @param {string} videoId - Youtube Id
 * @param {string} title - Title of the video
 * @return {iframe}
 */
const createIframe = function (videoId, title) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('src', `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`);
  iframe.setAttribute('width', '418');
  iframe.setAttribute('height', '742');
  iframe.setAttribute('title', title);
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write;'
  + ' encrypted-media; gyroscope; picture-in-picture');
  iframe.setAttribute('allowfullscreen', '');
  return iframe;
};
const modalTemplate = `
  <div id="shorts-modal" class="shorts-modal">
    <div class="shorts-modal-inner" role="dialog">
      <div class="shorts-modal-buttons">
        <button type="button" class="shorts-modal-button shorts-modal-button-prev">
          <svg class="shorts-modal-button-icon" width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <path d="M 67 0 L 17 50 L 67 100 L 83 84 L 49 50 L 83 16 L 67 0 Z"/>
          </svg>
          <span class="shorts-modal-button-text">Previous</span>
        </button>
        <button type="button" class="shorts-modal-button shorts-modal-button-next">
          <svg class="shorts-modal-button-icon" width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <path d="M 33 0 L 83 50 L 33 100 L 17 84 L 51 50 L 17 16 L 33 0 Z"/>
          </svg>
          <span class="shorts-modal-button-text">Next</span>
        </button>
      </div>
      <div class="shorts-modal-content"></div>
      <div aria-hidden="true" class="shorts-modal-indicators"></div>
      <button type="button" class="shorts-modal-button shorts-modal-button-close">
        <svg class="shorts-modal-button-icon" width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path id="Cross" d="M 84.152718 100 L 50 65.846855 L 15.846903 100 L 0 84.153145 L 34.153191 50 L 0 15.846855 L 15.846903 0 L 50 34.153145 L 84.152718 0 L 99.998489 15.846855 L 65.846901 50 L 99.998489 84.153145 Z"/>
        </svg>
        <span class="shorts-modal-button-text">Close</span>
      </button>
    </div>
  </div>`;

/**
   *
   * @return {Object} Object with open, next, previous, close, videoListSet, videoListClear, videoList methods
   */
const shortsPlayer = function () {
  const selector = '.shorts-modal';

  document.body.insertAdjacentHTML('beforeend', modalTemplate);

  let videos = [];
  let currentId = null;
  let activeElement = null;

  const modal = document.querySelector(selector);
  const modalContent = modal.querySelector('.shorts-modal-content');
  const modalClose = modal.querySelector('.shorts-modal-button-close');
  const modalNext = modal.querySelector('.shorts-modal-button-next');
  const modalPrev = modal.querySelector('.shorts-modal-button-prev');
  const modalActiveClass = 'shorts-modal-active';
  const indicatorActiveClass = 'indicator-active';

  const open = function (videoId) {
    activeElement = document.activeElement;
    modal.classList.add(modalActiveClass);
    setupIndicators();
    setPlayer(videoId);
    modalNext.focus();
    document.body.classList.add('shorts-modal-open');
  };

  const close = function () {
    modal.classList.remove(modalActiveClass);
    modalContent.innerHTML = '';
    document.body.classList.remove('shorts-modal-open');
    videos = [];
    if (activeElement) {
      activeElement.focus();
    }
  };

  const next = function () {
    const nextId = getNextId();
    if (nextId) setPlayer(nextId);
  };

  const previous = function () {
    const prevId = getPreviousId();
    if (prevId) setPlayer(prevId);
  };

  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      close();
    }
  });

  modalClose.addEventListener('click', (e) => {
    e.preventDefault();
    close();
  });

  modalNext.addEventListener('click', (e) => {
    e.preventDefault();
    next();
  });

  modalPrev.addEventListener('click', (e) => {
    e.preventDefault();
    previous();
  });

  modal.addEventListener('keydown', (e) => {
    // focus trap
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === modalPrev) {
          e.preventDefault();
          modalClose.focus();
        }
      }
      else {
        if (document.activeElement === modalClose) {
          e.preventDefault();
          modalPrev.focus();
        }
      };
    };

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      previous();
    }
  });

  const getIndexOfCurrentId = function () {
    return videos.indexOf(currentId);
  };

  const getNextId = function () {
    const index = getIndexOfCurrentId();
    return videos[index + 1];
  };

  const getPreviousId = function () {
    const index = getIndexOfCurrentId();
    return videos[index - 1];
  };

  const setPlayer = function (videoId) {
    modalContent.innerHTML = '';
    modalContent.appendChild(createIframe(videoId, 'Shorts Player'));
    currentId = videoId;
    setActiveIndicator();
  };

  const setupIndicators = function () {
    const indicators = modal.querySelector('.shorts-modal-indicators');
    indicators.innerHTML = '';
    videos.forEach(() => {
      const indicator = document.createElement('span');
      indicator.classList.add('shorts-modal-indicator');
      indicators.appendChild(indicator);
    });
  };

  const setActiveIndicator = function () {
    const index = getIndexOfCurrentId();
    const indicators = modal.querySelectorAll('.shorts-modal-indicator');
    indicators.forEach((indicator, i) => {
      if (i === index) {
        indicator.classList.add(indicatorActiveClass);
      }
      else {
        indicator.classList.remove(indicatorActiveClass);
      }
    });
  };

  return {
    open,
    next,
    previous,
    close,
    videoListSet: function (videoIds) {
      videos = videoIds;
    },
    videoListClear: function () {
      videos = [];
    },
    videoList: function () {
      return videos;
    },
  };
};

const player = shortsPlayer();

/**
   * Initialize the shorts modal with the given options
   *
   * @param {object} options Object with selector and itemDataAttribute
   * @param {string} selector Selector of the shorts container
   * @param {string} itemDataAttribute Data attribute used to provide the
   * video id, the default is 'data-ytshort-id'
   */
const shortsModal = function ({
  selector = '.shorts',
  itemDataAttribute = 'data-ytshort-id' } = {}) {
  const shorts = document.querySelectorAll(selector);

  const attrSelector = `[${itemDataAttribute}]`;

  shorts.forEach((short) => {
    const els = short.querySelectorAll(attrSelector);
    const videoIds = Array.from(els).map(
      (el) => {
        return el.getAttribute(itemDataAttribute);
      });
    short.addEventListener('click', (e) => {
      if (!e.target.closest(attrSelector)) {
        return;
      }
      e.preventDefault();
      const videoId = e.target.closest(attrSelector)
        .getAttribute(itemDataAttribute);
      player.videoListSet(videoIds);
      player.open(videoId);
    });

    // initialise the pager
    makePager(short);
  });
};

export default shortsModal;
